import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Send, ImagePlus, Loader2, X } from 'lucide-react';
import { t } from '../../lib/i18n';

export const PhotoPreviewChip = ({ src, onClear }) => (
  <div className="px-4 sm:px-6 pb-2" data-testid="chat-photo-preview">
    <div className="inline-flex items-center gap-2 p-2 rounded-lg bg-white/5 border border-white/10">
      <img src={src} alt="Preview" className="w-16 h-16 object-cover rounded" />
      <button data-testid="chat-photo-clear-button" onClick={onClear} className="p-1 hover:bg-white/10 rounded" aria-label="Remove photo">
        <X className="w-4 h-4 text-white/60" />
      </button>
    </div>
  </div>
);

export const ChatComposer = ({ language, input, setInput, loading, canSend, onSend, onSelectPhoto }) => {
  const fileInputRef = useRef(null);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleFileChange = (e) => {
    onSelectPhoto(e.target.files?.[0]);
    e.target.value = '';
  };

  return (
    <div className="px-4 sm:px-6 py-3 border-t border-white/[0.06] bg-lux-obsidian/50 backdrop-blur-md" data-testid="chat-composer">
      <div className="flex items-end gap-2 max-w-4xl mx-auto">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
          data-testid="chat-photo-file-input"
        />
        <Button
          data-testid="chat-attach-photo-button"
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
          className="text-white/40 hover:text-[var(--lux-gold)] hover:bg-white/5 flex-shrink-0"
          size="icon"
          disabled={loading}
          aria-label={t('attachPhoto', language)}
        >
          <ImagePlus className="w-5 h-5" />
        </Button>
        <Textarea
          data-testid="chat-message-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('typeMessage', language)}
          className="min-h-[44px] max-h-32 resize-none bg-black/20 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[var(--lux-gold)] focus-visible:ring-offset-[var(--lux-obsidian)]"
          rows={1}
          disabled={loading}
        />
        <Button
          data-testid="chat-send-button"
          onClick={onSend}
          disabled={loading || !canSend}
          className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] flex-shrink-0 active:scale-[0.98] transition-[background-color,transform] duration-200"
          size="icon"
          aria-label={t('send', language)}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};
